import React, { useState } from "react";
import portalApi from "../../api/portalClient";
import { usePortalAuth } from "../../context/PortalAuthContext";

/**
 * Customer-portal self-service 2FA. Mirrors the staff Security page
 * minus API tokens / push notifications — portal users don't get those.
 *
 *   1. Click "Enable 2FA" → POST /portal/2fa/enroll → modal shows QR + secret.
 *   2. User scans with authenticator app, enters first 6-digit code.
 *   3. POST /portal/2fa/verify → backend flips totp_enabled and returns
 *      one-time backup codes; we surface them with a clear save-now notice.
 *
 * Disabling requires a fresh TOTP/backup code so a transient session
 * hijack can't turn off the second factor.
 */

type EnrollState =
  | { stage: "idle" }
  | { stage: "enrolling"; secret: string; otpauth_uri: string; qr_data_url: string }
  | { stage: "show-codes"; codes: string[] };

export default function PortalSecurity() {
  const { user, refresh } = usePortalAuth();
  const [enrollState, setEnrollState] = useState<EnrollState>({ stage: "idle" });
  const [verifyCode, setVerifyCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disabling, setDisabling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [enabledOverride, setEnabledOverride] = useState<boolean | null>(null);
  const totpEnabled = enabledOverride ?? !!user?.totp_enabled;

  const startEnroll = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await portalApi.post("/api/v1/portal/2fa/enroll");
      setEnrollState({
        stage: "enrolling",
        secret: data.secret,
        otpauth_uri: data.otpauth_uri,
        qr_data_url: data.qr_data_url,
      });
      setVerifyCode("");
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Couldn't start enrollment.");
    } finally {
      setBusy(false);
    }
  };

  const submitVerify = async () => {
    if (enrollState.stage !== "enrolling") return;
    setError("");
    setBusy(true);
    try {
      const { data } = await portalApi.post("/api/v1/portal/2fa/verify", {
        code: verifyCode.trim(),
      });
      setEnrollState({ stage: "show-codes", codes: data.backup_codes });
      setEnabledOverride(true);
      refresh().catch(() => {});
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ||
          "That code didn't verify. Check the authenticator app and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const cancelEnroll = () => {
    setEnrollState({ stage: "idle" });
    setVerifyCode("");
    setError("");
    setShowSecret(false);
  };

  const submitDisable = async () => {
    setError("");
    setBusy(true);
    try {
      await portalApi.post("/api/v1/portal/2fa/disable", { code: disableCode.trim() });
      setEnabledOverride(false);
      setDisabling(false);
      setDisableCode("");
      refresh().catch(() => {});
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Couldn't disable MFA. Code rejected.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">Security</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
        Manage multi-factor authentication for your portal account.
      </p>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="h-1 w-full bg-brand" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Multi-factor authentication
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                Add a 6-digit code from an authenticator app on top of your password. Strongly
                recommended for any portal account.
              </p>
            </div>
            <span
              className={`shrink-0 text-[11px] px-2.5 py-1 rounded-full font-semibold border ${
                totpEnabled
                  ? "bg-green-50 dark:bg-green-900/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600"
              }`}
            >
              {totpEnabled ? "Enabled" : "Disabled"}
            </span>
          </div>

          {!totpEnabled && enrollState.stage === "idle" && (
            <div className="mt-4">
              <button
                onClick={startEnroll}
                disabled={busy}
                className="bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-5 py-2 rounded-lg shadow-sm disabled:opacity-50 transition-colors"
              >
                {busy ? "Starting…" : "Enable MFA"}
              </button>
            </div>
          )}

          {totpEnabled && !disabling && (
            <div className="mt-4">
              <button
                onClick={() => {
                  setDisabling(true);
                  setDisableCode("");
                  setError("");
                }}
                className="border border-gray-300 dark:border-gray-600 text-sm font-medium px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
              >
                Disable MFA
              </button>
            </div>
          )}

          {totpEnabled && disabling && (
            <div className="mt-4 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Enter a current 6-digit code from your authenticator app (or a backup code) to
                confirm.
              </p>
              <input
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                placeholder="123456"
                autoComplete="one-time-code"
                className="w-48 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2 text-sm font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              />
              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}
              <div className="flex items-center gap-2">
                <button
                  onClick={submitDisable}
                  disabled={busy || !disableCode}
                  className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                >
                  {busy ? "Disabling…" : "Confirm disable"}
                </button>
                <button
                  onClick={() => {
                    setDisabling(false);
                    setDisableCode("");
                    setError("");
                  }}
                  className="border border-gray-300 dark:border-gray-600 text-sm px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {error && enrollState.stage === "idle" && !disabling && (
            <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mt-3">
              {error}
            </p>
          )}
        </div>
      </div>

      {/* Enrollment modal */}
      {enrollState.stage === "enrolling" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto overflow-hidden">
            <div className="h-1 w-full bg-brand" />
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Set up multi-factor authentication
              </h2>
              <ol className="list-decimal pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-2 leading-relaxed">
                <li>
                  Open your authenticator app (Google Authenticator, Authy, 1Password, etc.) and
                  add a new account.
                </li>
                <li>Scan the QR code below — or paste the secret manually.</li>
                <li>Enter the 6-digit code the app shows to finish.</li>
              </ol>

              <div className="flex justify-center">
                <img
                  src={enrollState.qr_data_url}
                  alt="MFA QR code"
                  className="w-44 h-44 border border-gray-200 dark:border-gray-700 rounded-lg"
                />
              </div>

              <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-xs">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Manual secret</span>
                  <button
                    onClick={() => setShowSecret(!showSecret)}
                    className="text-brand dark:text-brand-light hover:underline font-medium"
                  >
                    {showSecret ? "Hide" : "Show"}
                  </button>
                </div>
                <div className="font-mono break-all text-gray-800 dark:text-gray-200">
                  {showSecret ? enrollState.secret : "â€¢".repeat(enrollState.secret.length)}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">
                  Code from app
                </label>
                <input
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value)}
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded-lg px-3 py-2.5 text-lg font-mono tracking-widest text-center mt-1.5 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg">
                  {error}
                </p>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={cancelEnroll}
                  className="border border-gray-300 dark:border-gray-600 text-sm font-medium px-4 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitVerify}
                  disabled={busy || verifyCode.length !== 6}
                  className="bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm disabled:opacity-50 transition-colors"
                >
                  {busy ? "Verifying…" : "Verify & enable"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backup-codes display */}
      {enrollState.stage === "show-codes" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto overflow-hidden">
            <div className="h-1 w-full bg-green-500" />
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-green-700 dark:text-green-300">
                MFA enabled — save your backup codes
              </h2>
              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                Each of these codes can be used <strong>once</strong> if you lose access to your
                authenticator app. Save them somewhere safe — they will <strong>not</strong> be
                shown again.
              </p>
              <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4 font-mono text-sm grid grid-cols-2 gap-2">
                {enrollState.codes.map((c) => (
                  <div key={c} className="tracking-wider text-gray-800 dark:text-gray-200">
                    {c}
                  </div>
                ))}
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(enrollState.codes.join("\n")).catch(() => {});
                }}
                className="text-sm text-brand dark:text-brand-light hover:underline font-medium"
              >
                Copy all codes to clipboard
              </button>
              <div className="flex justify-end pt-2">
                <button
                  onClick={() => {
                    setEnrollState({ stage: "idle" });
                    setVerifyCode("");
                    setError("");
                  }}
                  className="bg-brand hover:bg-brand-dark text-white text-sm font-semibold px-4 py-2 rounded-lg shadow-sm transition-colors"
                >
                  I've saved them
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
